import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, AlertCircle, CheckCircle, Trash2, Loader2, Search, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import ReportHeadersPanel from '../components/ReportHeadersPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEARCH_TRENDS_TAB = '__search_trends__';

// Static hierarchy for Flipkart
const FLIPKART_HIERARCHY = {
  adTypes: {
    'Flipkart Ads': {
      products: {
        'PLA': ['Consolidated Daily Report (Seller Dashboard)', 'Consolidated Daily Report (Ads Portal)'],
        'PCA': ['Consolidated Daily Report'],
        'Brand Ads': ['Consolidated Report'],
      }
    },
    'Off Platform Ads': {
      products: {
        'Telegram Ads': ['Consolidated Report'],
        'Whatsapp Ads': ['Consolidated Report'],
        'Google Ads': ['Product Report'],
        'Meta Ads': ['Consolidated Report'],
        'Meta Ads by FK': ['Consolidated Report'],
        'Criteo by FK': ['Consolidated Report'],
      }
    }
  }
};

// Static hierarchy for Amazon
const AMAZON_HIERARCHY = {
  adTypes: {
    'Amazon Ads': {
      products: {
        'Sponsored Products': ['Consolidated Report'],
        'Sponsored Brand': ['Consolidated Report'],
        'Sponsored Display': ['Consolidated Report'],
        'ONO DSP': ['Consolidated Report'],
      }
    },
    'Off Platform Ads': {
      products: {
        '3P DSP': ['Consolidated Report'],
        'Telegram Ads': ['Consolidated Report'],
        'Whatsapp Ads': ['Consolidated Report'],
        'Google Ads': ['Product Report'],
        'Meta Ads': ['Consolidated Report'],
      }
    }
  }
};

// Static hierarchy for Blinkit — single "On-Platform Ads" umbrella that
// branches into Performance & Reach tracks. To keep the existing 4-dropdown
// cascade (Channel → Ad Type → Ad Product → Report Type) usable, Performance
// and Reach are flat ad_types here; the Ad Properties page wraps them under
// an "On-Platform Ads" header in its drill. No parser registered yet.
const BLINKIT_HIERARCHY = {
  adTypes: {
    'On-Platform Ads — Performance': {
      products: {
        'Product Booster':    ['Consolidated Report'],
        'Recommendation Ads': ['Consolidated Report'],
        'Product Shelf':      ['Consolidated Report'],
      }
    },
    'On-Platform Ads — Reach': {
      products: {
        'Stories':            ['Consolidated Report'],
        'Prime Banner':       ['Consolidated Report'],
        'Brand Spotlight':    ['Consolidated Report'],
        'Listing Spotlight':  ['Consolidated Report'],
        'Brand Booster':      ['Consolidated Report'],
      }
    }
  }
};

// Static hierarchy for Zepto — mirrors Amazon Ads sub-tree (same SP/SB/SD report shape).
const ZEPTO_HIERARCHY = {
  adTypes: {
    'Zepto Ads': {
      products: {
        'Sponsored Products': ['Consolidated Report'],
        'Sponsored Brand': ['Consolidated Report'],
        'Sponsored Display': ['Consolidated Report'],
      }
    }
  }
};

// Static hierarchy for D2C — three Ad Channels, each with its own Property list.
// Parsers for these aren't registered yet; the Upload Center will surface the
// cascade but the backend will return a clean "No parser for ..." error until
// the respective parser is wired (pending source-file samples).
const D2C_HIERARCHY = {
  adTypes: {
    'Meta Ads': {
      products: {
        'Instagram':      ['Consolidated Report'],
        'Facebook':       ['Consolidated Report'],
        'Threads':        ['Consolidated Report'],
      }
    },
    'Google Ads': {
      products: {
        'YouTube':         ['Consolidated Report'],
        'Search Network':  ['Consolidated Report'],
      }
    },
    'WhatsApp': {
      products: {
        'WhatsApp':        ['Consolidated Report'],
        'Automation':      ['Consolidated Report'],
        'Manual':          ['Consolidated Report'],
      }
    },
  }
};

export default function UploadCenter({ user }) {
  const [channels, setChannels] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);

  // Flipkart-specific filters
  const [selectedAdType, setSelectedAdType] = useState('');
  const [selectedAdProduct, setSelectedAdProduct] = useState('');
  const [selectedReportType, setSelectedReportType] = useState('');
  const [reportMonth, setReportMonth] = useState(''); // YYYY-MM for consolidated reports
  // Day-range mode (3P DSP) — two `type=date` inputs. Backend distributes the
  // file's cumulative totals equally across every day in the picked window.
  const [dateRangeStart, setDateRangeStart] = useState(''); // YYYY-MM-DD
  const [dateRangeEnd, setDateRangeEnd] = useState('');     // YYYY-MM-DD
  const [uploading, setUploading] = useState(false);
  // Report-headers preview panel — toggled by "View Format" next to Report Type.
  const [showHeadersPreview, setShowHeadersPreview] = useState(false);

  useEffect(() => { fetchChannels(); }, []);

  useEffect(() => {
    if (selectedChannel) fetchUploadHistory();
  }, [selectedChannel, selectedAdType, selectedAdProduct, selectedReportType]); // eslint-disable-line

  // Reset downstream filters on change
  useEffect(() => { setSelectedAdProduct(''); setSelectedReportType(''); }, [selectedAdType]);
  useEffect(() => { setSelectedReportType(''); setReportMonth(''); setDateRangeStart(''); setDateRangeEnd(''); }, [selectedAdProduct]);

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/channels`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChannels(data);
        if (data.length > 0) setSelectedChannel(data[0].channel_id);
      }
    } catch (e) { console.error(e); }
  };

  const fetchUploadHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedChannel) params.set('channel_id', selectedChannel);
      if (selectedAdType) params.set('ad_type', selectedAdType);
      if (selectedAdProduct) params.set('ad_product', selectedAdProduct);
      if (selectedReportType) params.set('report_type', selectedReportType);
      const res = await fetch(`${API}/upload-history?${params.toString()}`, { credentials: 'include' });
      if (res.ok) setUploadHistory(await res.json());
    } catch (e) { console.error(e); }
  };

  const deleteHistoryRecord = async (uploadId) => {
    if (!window.confirm('Delete this upload history record?')) return;
    try {
      const res = await fetch(`${API}/upload-history/${uploadId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success('Record deleted');
        fetchUploadHistory();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Get current channel name
  const currentChannelName = channels.find(c => c.channel_id === selectedChannel)?.name || '';
  const isFlipkart = currentChannelName.toLowerCase() === 'flipkart';
  const isAmazon = currentChannelName.toLowerCase() === 'amazon';
  const isZepto = currentChannelName.toLowerCase() === 'zepto';
  const isBlinkit = currentChannelName.toLowerCase() === 'blinkit';
  const isD2C = currentChannelName.toLowerCase() === 'd2c';
  const hierarchy = isFlipkart ? FLIPKART_HIERARCHY
    : isAmazon ? AMAZON_HIERARCHY
    : isZepto ? ZEPTO_HIERARCHY
    : isBlinkit ? BLINKIT_HIERARCHY
    : isD2C ? D2C_HIERARCHY
    : null;

  // Compute options based on selections
  const adTypeOptions = hierarchy ? Object.keys(hierarchy.adTypes) : [];
  const adProductOptions = selectedAdType && hierarchy
    ? Object.keys(hierarchy.adTypes[selectedAdType]?.products || {})
    : [];
  const reportTypeOptions = selectedAdProduct && selectedAdType && hierarchy
    ? hierarchy.adTypes[selectedAdType]?.products?.[selectedAdProduct] || []
    : [];

  // Consolidated/Product reports for Amazon, Telegram, and Google auto-detect their
  // date range from the file (or filename). All other channels need a Report Month picker.
  const isConsolidatedReport = selectedReportType && !selectedReportType.toLowerCase().includes('daily');
  const isTelegram = selectedAdProduct === 'Telegram Ads';
  const isGoogle = selectedAdProduct === 'Google Ads';
  // Meta Ads by FK / Criteo by FK: the campaign-name carries the date range
  // inline (e.g. `..._May12_May31_...`), so the backend auto-detects it. No
  // user input needed — same behaviour as Telegram/Google/Amazon.
  const isFkAutoDated = selectedAdProduct === 'Meta Ads by FK' || selectedAdProduct === 'Criteo by FK';
  // 3P DSP: source file is cumulative — user picks a date range (start + end day)
  // and backend distributes spend/revenue equally across every day in the window.
  // Zepto SP / SB / SD: same cumulative shape (no Day column on the file),
  // so the date-range picker is used here too. Backend distributes per-day.
  // Flipkart Off Platform (all except Telegram): user-picked range always overrides file dates.
  const isFkOffPlatformPicked = isFlipkart && selectedAdType === 'Off Platform Ads' && !!selectedAdProduct && !isTelegram;
  const needsDateRange = selectedAdProduct === '3P DSP' || isZepto || isFkOffPlatformPicked;
  // D2C Google Ads: file has per-day Day column → no Report Month required.
  const isD2CGoogle = isD2C && selectedAdType === 'Google Ads';
  // D2C Meta Ads: per user request — Report Month is not required for the
  // Meta Ads ad-type (Instagram / Facebook / Threads). The parser will pick
  // dates from the file itself.
  const isD2CMeta = isD2C && selectedAdType === 'Meta Ads';
  // D2C WhatsApp (Automation / Manual): wide-monthly export — parser reads
  // month headers from the file itself, so no Report Month picker needed.
  const isD2CWhatsApp = isD2C && selectedAdType === 'WhatsApp'
    && (selectedAdProduct === 'Automation' || selectedAdProduct === 'Manual');
  // Blinkit: per-day Date column on every sheet of the Blinkit dump → the
  // backend reads dates straight from the file, so the Report Month picker
  // is not required (and the user explicitly asked for it removed).
  const isBlinkitConsolidated = isBlinkit && isConsolidatedReport;
  // Amazon ONO DSP: the file has NO per-day breakdown (only Insertion Order
  // Starts/Ends spanning multiple months), so the user must pick a Report
  // Month; the backend fans each row across every day of that month.
  const isAmazonOnoDsp = isAmazon && selectedAdType === 'Amazon Ads' && selectedAdProduct === 'ONO DSP';
  const needsReportMonth = (isConsolidatedReport && !isAmazon && !isZepto && !isD2CGoogle && !isD2CMeta && !isD2CWhatsApp && !isTelegram && !isGoogle && !isFkAutoDated && !needsDateRange && !isBlinkitConsolidated)
    || isAmazonOnoDsp;
  const canUpload = hierarchy
    ? (!!selectedReportType
        && (!needsReportMonth || !!reportMonth)
        && (!needsDateRange || (!!dateRangeStart && !!dateRangeEnd && dateRangeStart <= dateRangeEnd)))
    : false;

  const handleFileUpload = async (file, confirmOverlap = false) => {
    if (!canUpload) {
      toast.error('Please complete all filter selections');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('channel_id', selectedChannel);
    formData.append('channel_name', currentChannelName);
    formData.append('ad_type', selectedAdType);
    formData.append('ad_product', selectedAdProduct);
    formData.append('report_type', selectedReportType);
    if (reportMonth) formData.append('report_month', reportMonth);
    // Date Range mode (3P DSP) — send day-level start + end as YYYY-MM-DD.
    if (needsDateRange && dateRangeStart && dateRangeEnd) {
      formData.append('date_range_start', dateRangeStart);
      formData.append('date_range_end', dateRangeEnd);
    }
    if (confirmOverlap) formData.append('confirm_overlap', 'true');

    // Show "Processing" in the table immediately by refetching after a short delay
    const earlyRefresh = setTimeout(() => fetchUploadHistory(), 250);

    try {
      const res = await fetch(`${API}/upload/flipkart-report`, {
        method: 'POST', credentials: 'include', body: formData
      });
      clearTimeout(earlyRefresh);
      if (res.status === 409) {
        const err = await res.json();
        const d = err.detail || {};
        const ex = d.existing || {};
        const prop = d.proposed || {};
        const msg = `An overlapping upload already exists.\n\n` +
          `Existing: ${ex.file_name || '(unnamed)'} — ${ex.date_range_start} to ${ex.date_range_end}\n` +
          `New: ${file.name} — ${prop.date_range_start} to ${prop.date_range_end}\n\n` +
          `Re-uploading will replace overlapping data. Continue?`;
        if (window.confirm(msg)) {
          await handleFileUpload(file, true);
        } else {
          toast.message('Upload cancelled');
        }
        fetchUploadHistory();
        return;
      }
      if (!res.ok) {
        const err = await res.json();
        const detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        toast.error('Upload failed: ' + detail);
        fetchUploadHistory();
        return;
      }
      const result = await res.json();
      const log = result.validation_log;
      toast.success(
        <div>
          <div className="font-semibold">{result.message}</div>
          <div className="text-xs mt-1 text-gray-600">
            Processed: {log.rows_processed} | Inserted: {log.rows_inserted} | Duplicates removed: {log.duplicates_removed}
          </div>
        </div>
      );
      fetchUploadHistory();
    } catch (e) {
      console.error(e);
      toast.error('Upload failed');
      fetchUploadHistory();
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((files) => {
    if (files.length > 0) handleFileUpload(files[0]);
  }, [selectedChannel, selectedAdType, selectedAdProduct, selectedReportType]); // eslint-disable-line

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    multiple: false
  });

  const getChannelColor = (i) => ['#32B4AC','#f59e0b','#ef4444','#8b5cf6','#3b82f6'][i % 5];

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="upload" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-5 shadow-sm">
          <h1 className="text-2xl font-bold text-[#0F0F0F]">Upload Center</h1>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <Tabs value={selectedChannel} onValueChange={(v) => { setSelectedChannel(v); setSelectedAdType(''); }} className="space-y-6">
              <TabsList className="bg-white border border-gray-200 p-1">
                {channels.map((ch, i) => (
                  <TabsTrigger key={ch.channel_id} value={ch.channel_id}
                    className="data-[state=active]:bg-white"
                    style={{ borderBottom: selectedChannel === ch.channel_id ? `3px solid ${getChannelColor(i)}` : 'none' }}>
                    <span style={{ color: selectedChannel === ch.channel_id ? getChannelColor(i) : '#6B7280' }}>{ch.name}</span>
                  </TabsTrigger>
                ))}
                <TabsTrigger
                  key={SEARCH_TRENDS_TAB}
                  value={SEARCH_TRENDS_TAB}
                  className="data-[state=active]:bg-white"
                  style={{ borderBottom: selectedChannel === SEARCH_TRENDS_TAB ? '3px solid #32B4AC' : 'none' }}
                  data-testid="upload-tab-search-trends"
                >
                  <span className="flex items-center gap-1.5" style={{ color: selectedChannel === SEARCH_TRENDS_TAB ? '#32B4AC' : '#6B7280' }}>
                    <Search className="w-3.5 h-3.5" />
                    Search Trends
                  </span>
                </TabsTrigger>
              </TabsList>

              {channels.map(channel => (
                <TabsContent key={channel.channel_id} value={channel.channel_id} className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    {/* Filter Row */}
                    <div className="flex flex-wrap items-end gap-4 mb-6">
                      {/* Ad Type */}
                      {hierarchy && (
                        <div className="w-48">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Ad Type</label>
                          <Select value={selectedAdType || 'none'} onValueChange={(v) => setSelectedAdType(v === 'none' ? '' : v)}>
                            <SelectTrigger className="bg-white border-gray-300" data-testid="filter-ad-type">
                              <SelectValue placeholder="Select Ad Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Ad Type</SelectItem>
                              {adTypeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Ad Product */}
                      {hierarchy && selectedAdType && (
                        <div className="w-48">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Ad Product</label>
                          <Select value={selectedAdProduct || 'none'} onValueChange={(v) => setSelectedAdProduct(v === 'none' ? '' : v)}>
                            <SelectTrigger className="bg-white border-gray-300" data-testid="filter-ad-product">
                              <SelectValue placeholder="Select Ad Product" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Ad Product</SelectItem>
                              {adProductOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Report Type */}
                      {hierarchy && selectedAdProduct && (
                        <div className="w-72">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Report Type</label>
                          <div className="flex items-center gap-1.5">
                            <Select value={selectedReportType || 'none'} onValueChange={(v) => { setSelectedReportType(v === 'none' ? '' : v); setReportMonth(''); }}>
                              <SelectTrigger className="bg-white border-gray-300 flex-1" data-testid="filter-report-type">
                                <SelectValue placeholder="Select Report Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select Report Type</SelectItem>
                                {reportTypeOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {/* View Format toggle — shows the expected column headers
                                for the current (channel, ad_type, ad_product, report_type)
                                selection. Disabled until a Report Type is picked. */}
                            <button
                              type="button"
                              onClick={() => setShowHeadersPreview(v => !v)}
                              disabled={!selectedReportType}
                              title={selectedReportType ? 'Preview expected column headers' : 'Pick a Report Type first'}
                              className={`inline-flex items-center justify-center gap-1 h-10 px-2.5 text-xs font-medium border rounded-md transition-colors ${
                                showHeadersPreview
                                  ? 'bg-[#32B4AC] text-white border-[#32B4AC] hover:bg-[#2a9d95]'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white`}
                              data-testid="view-format-btn"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span className="hidden xl:inline">View Format</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Report Month — for non-Amazon consolidated reports only.
                          Amazon auto-detects the date range from the uploaded file. */}
                      {needsReportMonth && (
                        <div className="w-44">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Report Month</label>
                          <input
                            type="month"
                            value={reportMonth}
                            onChange={(e) => setReportMonth(e.target.value)}
                            className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC] focus:border-transparent"
                            data-testid="filter-report-month"
                          />
                        </div>
                      )}

                      {/* Date Range — 3P DSP uploads cover a multi-day span and
                          file is fully cumulative; spend + revenue are distributed
                          equally across all days in the picked range server-side. */}
                      {needsDateRange && (
                        <>
                          <div className="w-44">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Start Date</label>
                            <input
                              type="date"
                              value={dateRangeStart}
                              onChange={(e) => setDateRangeStart(e.target.value)}
                              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC] focus:border-transparent"
                              data-testid="filter-date-range-start"
                            />
                          </div>
                          <div className="w-44">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">End Date</label>
                            <input
                              type="date"
                              value={dateRangeEnd}
                              min={dateRangeStart || undefined}
                              onChange={(e) => setDateRangeEnd(e.target.value)}
                              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC] focus:border-transparent"
                              data-testid="filter-date-range-end"
                            />
                          </div>
                        </>
                      )}

                      {/* Upload Button */}
                      <div className="ml-auto">
                        <Button
                          onClick={() => document.getElementById('file-input')?.click()}
                          disabled={!canUpload || uploading}
                          className="bg-[#32B4AC] hover:bg-[#2a9d95] text-white h-10"
                          data-testid="upload-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploading ? 'Uploading...' : 'Upload File'}
                        </Button>
                        <input id="file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])} />
                      </div>
                    </div>

                    {isFkOffPlatformPicked && (
                      <p className="text-xs text-gray-500 -mt-2 mb-4" data-testid="fk-offplatform-date-hint">
                        Start and End Date are required. The picked dates replace any dates in the file, and totals are spread evenly across each day.
                      </p>
                    )}

                    {/* Selection Summary */}
                    {selectedAdType && (
                      <div className="flex items-center gap-2 mb-4 text-sm">
                        <span className="px-2 py-0.5 bg-[#32B4AC]/10 text-[#32B4AC] rounded font-medium">{selectedAdType}</span>
                        {selectedAdProduct && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded font-medium">{selectedAdProduct}</span>
                          </>
                        )}
                        {selectedReportType && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">{selectedReportType}</span>
                          </>
                        )}
                        {reportMonth && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">{reportMonth}</span>
                          </>
                        )}
                        {dateRangeStart && dateRangeEnd && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium" data-testid="summary-date-range">{dateRangeStart} → {dateRangeEnd}</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Report Headers Preview — inline panel, live-updates as
                        selections change. Same component is reusable in any
                        future upload modal. */}
                    <ReportHeadersPanel
                      channel={currentChannelName}
                      adType={selectedAdType}
                      adProduct={selectedAdProduct}
                      reportType={selectedReportType}
                      open={showHeadersPreview && !!selectedReportType}
                      onClose={() => setShowHeadersPreview(false)}
                    />

                    {/* Dropzone */}
                    {canUpload ? (
                      <div {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                          isDragActive ? 'border-[#32B4AC] bg-[#32B4AC]/5' : 'border-gray-300 hover:border-[#32B4AC] hover:bg-gray-50'
                        }`}>
                        <input {...getInputProps()} />
                        <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-700 font-medium mb-1">
                          {isDragActive ? 'Drop file here' : 'Drag & drop file here, or click to browse'}
                        </p>
                        <p className="text-sm text-gray-500">Supports CSV, XLS, XLSX</p>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                        <Upload className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-400 font-medium">
                          {!hierarchy ? 'Upload not configured for this channel yet' : 'Select Ad Type, Ad Product, and Report Type to upload'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload History */}
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">Upload History</h3>
                      <span className="text-xs text-gray-400">Showing last 7 days</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="upload-history-table">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50/80">
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Upload Date</th>
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Data Range</th>
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Method</th>
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Details</th>
                            <th className="text-center py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider w-20">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadHistory.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-8 text-gray-400" data-testid="upload-history-empty">No upload history available</td></tr>
                          ) : uploadHistory.map(item => (
                            <tr key={item.upload_id} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`upload-history-row-${item.upload_id}`}>
                              <td className="py-2.5 px-4 text-gray-700">{new Date(item.uploaded_at).toLocaleString()}</td>
                              <td className="py-2.5 px-4 text-gray-700">{item.date_range_start && item.date_range_end ? `${item.date_range_start} → ${item.date_range_end}` : '—'}</td>
                              <td className="py-2.5 px-4 text-gray-700 capitalize">{item.upload_method}</td>
                              <td className="py-2.5 px-4">
                                {item.status === 'success' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                                    <CheckCircle className="w-3 h-3 mr-1" />Uploaded
                                  </span>
                                ) : item.status === 'updated' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                                    <CheckCircle className="w-3 h-3 mr-1" />Updated
                                  </span>
                                ) : item.status === 'processing' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                                    <AlertCircle className="w-3 h-3 mr-1" />Failed
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 px-4 text-gray-700 max-w-xs truncate" title={item.error_details || item.file_name || ''}>
                                {item.status === 'failed'
                                  ? (item.error_details || 'Unknown error')
                                  : item.file_name
                                    ? <span><span className="text-gray-900">{item.file_name}</span>{item.records_inserted ? <span className="text-gray-400"> · {item.records_inserted} records</span> : null}</span>
                                    : `${item.records_inserted || 0} records`}
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <button
                                  data-testid={`upload-history-delete-${item.upload_id}`}
                                  onClick={() => deleteHistoryRecord(item.upload_id)}
                                  className="text-gray-400 hover:text-red-500"
                                  title="Delete record">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </TabsContent>
              ))}

              <TabsContent key={SEARCH_TRENDS_TAB} value={SEARCH_TRENDS_TAB} className="space-y-6">
                <SearchTrendsUpload onUploaded={() => fetchUploadHistory()} />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
}

function SearchTrendsUpload({ onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleFile = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/upload/search-trends`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error('Upload failed: ' + (err.detail || res.statusText));
        return;
      }
      const result = await res.json();
      setLastResult(result);
      toast.success(result.message || 'Uploaded');
      onUploaded?.();
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((files) => {
    if (files.length > 0) handleFile(files[0]);
  }, []); // eslint-disable-line

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
  });

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-[#0F0F0F] flex items-center gap-2">
              <Search className="w-4 h-4 text-[#32B4AC]" />
              Search Trends Upload
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Required columns: <code className="bg-gray-100 px-1 rounded">category, channel, month (MM-YYYY), keyword, search_volume</code>
            </p>
          </div>
        </div>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-[#32B4AC] bg-[#32B4AC]/5' : 'border-gray-300 hover:border-[#32B4AC] hover:bg-gray-50'
          }`}
          data-testid="search-trends-upload-dropzone"
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <span>Uploading…</span>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-700 font-medium mb-1">
                {isDragActive ? 'Drop file here' : 'Drag & drop file here, or click to browse'}
              </p>
              <p className="text-sm text-gray-500">Supports CSV, XLS, XLSX</p>
            </>
          )}
        </div>

        {lastResult && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
            <div className="font-medium text-emerald-800">{lastResult.message}</div>
            <div className="text-xs text-emerald-700 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
              <span>Processed: {lastResult.validation_log?.rows_processed}</span>
              <span>Inserted: {lastResult.validation_log?.rows_inserted}</span>
              <span>Updated: {lastResult.validation_log?.rows_updated}</span>
              <span>Skipped: {lastResult.validation_log?.rows_skipped}</span>
              {lastResult.validation_log?.new_categories?.length > 0 && (
                <span>New categories: {lastResult.validation_log.new_categories.join(', ')}</span>
              )}
              {lastResult.validation_log?.new_channels?.length > 0 && (
                <span>New channels: {lastResult.validation_log.new_channels.join(', ')}</span>
              )}
            </div>
            {lastResult.validation_log?.errors?.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-amber-700">
                  {lastResult.validation_log.errors.length} row errors — view
                </summary>
                <ul className="mt-1 pl-4 list-disc text-amber-800">
                  {lastResult.validation_log.errors.slice(0, 20).map((er, i) => (
                    <li key={i}>Row {er.row}: {er.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
